export interface ClothingCatalogItemList {
  id: string;
  name: string;
  season: string;
  priceUsd: number;
  mandatory: boolean;
  active: boolean;
}

export interface ClothingCatalogItem extends ClothingCatalogItemList {
  serviceLifeWorkingDays: number;
  createdAt: string;
  updatedAt: string;
}

export interface ClothingCatalogItemPayload {
  name: string;
  season: string;
  serviceLifeWorkingDays: number;
  priceUsd: number;
  mandatory: boolean;
}

export interface ClothingMarkerNorm {
  id: string;
  markerCode: string;
  markerType: string;
  catalogItemId: string;
  quantity: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ClothingMarkerNormPayload {
  markerCode: string;
  markerType: string;
  catalogItemId: string;
  quantity: number;
  active?: boolean;
}

export interface ClothingMarker {
  markerCode: string;
  markerType: string;
}

export interface ClothingConfiguredMarker extends ClothingMarker {
  id: string;
  active: boolean;
}

export interface ClothingConfiguredMarkerPayload {
  markerCode: string;
  markerType: string;
  active?: boolean;
}

export interface ClothingSettings {
  storekeeperUserId: string;
  ceoUserId: string;
  officeDepartmentMarkerCode: string;
  planfixDepartmentMarkerFieldId: number;
  planfixJobMarkerFieldId: number;
  planfixEscalationParentTaskId: number;
  summerStartMonthDay: string;
  summerEndMonthDay: string;
  outOfStockExtensionMonths: number;
  refusalThreshold: number;
  refusalWindowMonths: number;
  headOverdueAfterExpiryDays: number;
  active: boolean;
}

export interface ClothingPersonalNorm {
  id: string;
  userId: string;
  catalogItemId: string;
  quantity: number;
  active?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface ClothingPersonalNormPayload {
  userId: string;
  catalogItemId: string;
  quantity: number;
}

export interface ClothingProfileStorekeeper {
  userId: string;
  fullName: string;
  phone: string;
}

export interface ClothingBiometry {
  growth?: number;
  weight?: number;
  clothingSize?: number;
  shoeSize?: number;
}

export interface EmployeeClothingOverview {
  userId: string;
  departmentMarkerCode: string;
  jobMarkerCode: string;
  biometry?: ClothingBiometry;
  storekeeper: ClothingProfileStorekeeper;
  positions: EmployeeClothingPosition[];
  activeRequests: ClothingRequest[];
  openLossCases: ClothingLossCase[];
}

export interface EmployeeClothingEntitlement {
  catalogItemId: string;
  catalogItemName: string;
  season: string;
  serviceLifeWorkingDays: number;
  priceUsd?: number;
  mandatory?: boolean;
  quantity?: number;
  entitledQuantity?: number;
  allowedQuantity?: number;
  effectiveAllowedQuantity?: number;
  activeQuantity?: number;
  requestAllowed?: boolean;
  totalGrantedWorkingDays?: number;
  consumedWorkingDays?: number;
  remainingWorkingDays?: number;
  status?: string;
  expired?: boolean;
  hasActiveRequest?: boolean;
  activeRequestId?: string;
  activeRequestStatus?: string;
  directIssueAllowed?: boolean;
  issueByRequestAllowed?: boolean;
  rejectNoStockAllowed?: boolean;
  temporarilyCoveredByNoStock?: boolean;
  temporaryNoStockCoverageUntil?: string;
}

export interface EmployeeClothingEntitlementsResponse {
  userId: string;
  departmentMarkerCode: string;
  jobMarkerCode: string;
  biometry?: ClothingBiometry;
  items: EmployeeClothingEntitlement[];
}

export type EmployeeClothingEntitlementsSource =
  EmployeeClothingEntitlement[] | EmployeeClothingEntitlementsResponse;

export interface EmployeeClothingPosition {
  positionId: string;
  catalogItemId: string;
  catalogItemName: string;
  season: string;
  serviceLifeWorkingDays: number;
  priceUsd: number;
  mandatory: boolean;
  totalGrantedWorkingDays: number;
  consumedWorkingDays: number;
  remainingWorkingDays: number;
  activeQuantity: number;
  lastIssueDate: string;
  firstExpiryDate: string;
  lastExpiryDate: string;
  activeOutOfStockUntil: string;
  status: string;
  expired: boolean;
  effectiveAllowedQuantity?: number;
  requestAllowed: boolean;
  hasActiveRequest?: boolean;
  activeRequestId?: string;
  activeRequestStatus?: string;
  directIssueAllowed?: boolean;
  issueByRequestAllowed?: boolean;
  rejectNoStockAllowed?: boolean;
  temporarilyCoveredByNoStock?: boolean;
  temporaryNoStockCoverageUntil?: string;
  updatedAt: string;
}

export interface ClothingRequest {
  id: string;
  userId: string;
  catalogItemId: string;
  catalogItemName: string;
  biometry?: ClothingBiometry;
  requestedQuantity: number;
  departmentMarkerCode: string;
  jobMarkerCode: string;
  effectiveAllowedQuantity: number;
  storekeeperUserId: string;
  status: string;
  rejectionStreak: number;
  lastNoStockRejectedAt: string;
  planfixEscalationTaskId: string;
  createdAt: string;
  updatedAt: string;
  closedAt: string;
}

export interface ClothingLossCase {
  id: string;
  userId: string;
  catalogItemId: string;
  catalogItemName: string;
  description?: string;
  positionRemainingWorkingDaysAtLoss: number;
  pricePerWorkingDayAtLoss: number;
  calculatedPenaltyUsd: number;
  calculatedPenaltyByn?: number;
  usdToBynRateAtDecision?: number;
  status: string;
  headUserId: string;
  headDecisionType?: string;
  headDecisionDescription?: string;
  ceoDecisionType?: string;
  ceoDecisionDescription?: string;
  decisionAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface ClothingRequestPayload {
  catalogItemId: string;
  requestedQuantity: number;
}

export interface ClothingLossCasePayload {
  catalogItemId: string;
  description: string;
}

export interface StorekeeperIssuePayload {
  issuedQuantity: number;
  issueDate?: string;
}

export interface StorekeeperRejectNoStockPayload {
  description: string;
}

export enum ClothingErrorCode {
  EmployeeRequestCreateCatalogItemNotFound = 12001,
  EmployeeRequestCreateSettingsNotConfigured = 12002,
  EmployeeRequestCreateMarkerNotConfigured = 12003,
  EmployeeRequestCreateNormNotConfigured = 12004,
  EmployeeRequestCreateStorekeeperNotAssigned = 12005,
  EmployeeRequestCreateAlreadyActiveRequest = 12006,
  EmployeeRequestCreateNotAllowedByState = 12007,
  EmployeeRequestCreateValidationFailed = 12008,
  EmployeeRequestCreateAccessDenied = 12009,
  EmployeeLossCaseCatalogItemNotFound = 12101,
  EmployeeLossCasePositionNotFound = 12102,
  EmployeeLossCaseAlreadyOpen = 12103,
  EmployeeLossCaseValidationFailed = 12104,
  EmployeeLossCaseAccessDenied = 12105,
  HeadLossDecisionCaseNotFound = 12201,
  HeadLossDecisionAlreadyDecided = 12202,
  HeadLossDecisionPositionNotFound = 12203,
  HeadLossDecisionCatalogItemNotFound = 12204,
  HeadLossDecisionAccessDenied = 12205,
  HeadLossDecisionValidationFailed = 12206,
  CeoLossDecisionCaseNotFound = 12501,
  CeoLossDecisionAlreadyDecided = 12502,
  CeoLossDecisionPositionNotFound = 12503,
  CeoLossDecisionCatalogItemNotFound = 12504,
  CeoLossDecisionAccessDenied = 12505,
  CeoLossDecisionValidationFailed = 12506,
  StorekeeperRequestNotFound = 12301,
  StorekeeperRequestConflict = 12302,
  StorekeeperRequestValidationFailed = 12303,
  StorekeeperAccessDenied = 12304,
  StorekeeperSettingsNotConfigured = 12305,
  AdminCatalogItemNotFound = 12401,
  AdminNormNotFound = 12402,
  AdminSettingsNotConfigured = 12403,
  AdminStorekeeperAssignmentNotFound = 12404,
  AdminConflict = 12405,
  AdminValidationFailed = 12406,
  AdminAccessDenied = 12407,
  CatalogItemNotFound = 12901,
  LossCaseNotFound = 12902,
  NormNotFound = 12903,
  PositionNotFound = 12904,
  RequestNotFound = 12905,
  SettingsNotConfigured = 12906,
  StorekeeperAssignmentNotFound = 12907,
  AccessDenied = 12908,
  RequestConflict = 12909,
  LossDecisionConflict = 12910,
  StorekeeperAssignmentConflict = 12911,
  MarkerNotConfigured = 12912,
  ValidationError = 12913,
  RequestValidationFailed = 12914,
  RequestParameterMissing = 12915,
  RequestParameterInvalid = 12916
}

export interface ClothingErrorResponse {
  errorCode: ClothingErrorCode;
  message: string;
}

export interface HeadClothingManualExtensionPayload {
  workingDays: number;
  reason: string;
}

export interface HeadPendingLossCase {
  lossCaseId: string;
  employeeId: string;
  employeeFullName: string;
  catalogItemId: string;
  catalogItemName: string;
  description?: string;
  positionRemainingWorkingDaysAtLoss: number;
  calculatedPenaltyUsd: number;
  createdAt: string;
}

export interface HeadClothingLossDecisionPayload {
  decisionType: string;
  description: string;
}

export interface CeoPendingLossCase {
  lossCaseId: string;
  employeeId: string;
  employeeFullName: string;
  catalogItemId: string;
  catalogItemName: string;
  employeeDescription?: string;
  headUserId: string;
  headDecisionDescription?: string;
  calculatedPenaltyUsd: number;
  calculatedPenaltyByn?: number;
  headDecisionAt: string;
}

export interface CeoClothingLossDecisionPayload {
  decisionType: string;
  description: string;
}

export interface ClothingMatrixCell {
  key: string;
  markerCode: string;
  markerType: string;
  quantity: number;
  normId: string;
  active: boolean;
}

export interface ClothingMatrixRow {
  item: ClothingCatalogItemList;
  cells: ClothingMatrixCell[];
}

export interface ClothingStorekeeperAssignment {
  assignmentId: string;
  storekeeperUserId: string;
  storekeeperFullName: string;
  groupId: string;
  groupName: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ClothingStorekeeperAssignmentPayload {
  storekeeperUserId: string;
  groupId: string;
  active: boolean;
}

export interface ClothingIssueStats {
  catalogItemId: string;
  catalogItemName: string;
  issuedQuantity: number;
  issueOperationsCount: number;
  totalIssuedUsd: number;
}

export interface ClothingIssueStatsFilters {
  from: string;
  to: string;
  catalogItemId: string;
  storekeeperUserId: string;
}

export interface ClothingOption {
  id: string;
  name: string;
}

export interface ClothingUserOption {
  id: string;
  fullName: string;
}

export interface StorekeeperEmployeeIssuedItem {
  catalogItemId: string;
  catalogItemName: string;
  activeQuantity: number;
  expired: boolean;
}

export interface StorekeeperEmployee {
  employeeId: string;
  employeeFullName: string;
  employeeLogin: string;
  groupId: string;
  groupName: string;
  departmentMarkerCode: string;
  issuedItems?: StorekeeperEmployeeIssuedItem[];
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/models/clothing.model.ts